class BrewGuide extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    const { signal } = this._abortController;

    this._tabs = [...this.querySelectorAll('[data-brew-tab]')];
    this._panels = [...this.querySelectorAll('[data-brew-panel]')];

    this._tabs.forEach((tab) => {
      tab.addEventListener('click', () => this._selectMethod(tab.dataset.brewTab), { signal });
      tab.addEventListener('keydown', (event) => this._onTabKeydown(event), { signal });
    });

    this._setupTimer(signal);
    this._setupEspresso(signal);
  }

  disconnectedCallback() {
    this._abortController?.abort();
    this._stopTicking();
  }

  _selectMethod(method) {
    this._tabs.forEach((tab) => {
      const selected = tab.dataset.brewTab === method;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      if (selected) tab.focus();
    });

    this._panels.forEach((panel) => {
      panel.hidden = panel.dataset.brewPanel !== method;
    });
  }

  _onTabKeydown(event) {
    const keys = { ArrowLeft: -1, ArrowRight: 1 };
    const step = keys[event.key];
    if (!step) return;

    event.preventDefault();
    const current = this._tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    const next = (current + step + this._tabs.length) % this._tabs.length;
    this._selectMethod(this._tabs[next].dataset.brewTab);
  }

  _setupTimer(signal) {
    this._filterPanel = this.querySelector('[data-brew-panel="filter"]');
    if (!this._filterPanel) return;

    this._clock = this._filterPanel.querySelector('[data-brew-clock]');
    this._progress = this._filterPanel.querySelector('[data-brew-progress]');
    this._steps = [...this._filterPanel.querySelectorAll('[data-brew-step]')];
    this._toggle = this._filterPanel.querySelector('[data-brew-toggle]');
    this._reset = this._filterPanel.querySelector('[data-brew-reset]');

    this._total = Number(this._filterPanel.dataset.totalSeconds) || 0;
    this._elapsed = 0;
    this._running = false;

    this._toggle?.addEventListener('click', () => this._onToggle(), { signal });
    this._reset?.addEventListener('click', () => this._resetTimer(), { signal });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this._stopTicking();
      else if (this._running) this._startTicking();
    }, { signal });

    this._renderTimer();
  }

  get _finished() {
    return this._elapsed >= this._total;
  }

  _onToggle() {
    if (this._finished) {
      this._resetTimer();
      return;
    }

    this._running = !this._running;

    if (this._running) this._startTicking();
    else this._stopTicking();

    this._renderTimer();
  }

  _resetTimer() {
    this._stopTicking();
    this._running = false;
    this._elapsed = 0;
    this._renderTimer();
  }

  _startTicking() {
    this._stopTicking();
    this._startedAt = Date.now() - this._elapsed * 1000;

    this._interval = setInterval(() => {
      this._elapsed = Math.min(this._total, Math.round((Date.now() - this._startedAt) / 1000));

      if (this._finished) {
        this._running = false;
        this._stopTicking();
      }

      this._renderTimer();
    }, 250);
  }

  _stopTicking() {
    clearInterval(this._interval);
    this._interval = null;
  }

  _formatTime(seconds) {
    return `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
  }

  _renderTimer() {
    if (this._clock) this._clock.textContent = this._formatTime(this._elapsed);

    if (this._progress) {
      const percentage = this._total > 0 ? Math.min(100, (this._elapsed / this._total) * 100) : 0;
      this._progress.style.width = `${percentage}%`;
    }

    let activeIndex = 0;
    this._steps.forEach((step, index) => {
      if (this._elapsed >= Number(step.dataset.time)) activeIndex = index;
    });

    this._steps.forEach((step, index) => {
      const isActive = index === activeIndex && !this._finished;
      step.classList.toggle('is-active', isActive);
      step.classList.toggle('is-done', index < activeIndex || this._finished);
    });

    if (this._toggle) {
      const { labelStart, labelPause, labelResume, labelRestart } = this._filterPanel.dataset;

      if (this._finished) this._toggle.textContent = labelRestart;
      else if (this._running) this._toggle.textContent = labelPause;
      else this._toggle.textContent = this._elapsed > 0 ? labelResume : labelStart;
    }

    if (this._reset) this._reset.hidden = this._elapsed === 0;

    this._filterPanel.classList.toggle('is-running', this._running);
    this._filterPanel.classList.toggle('is-finished', this._finished);
  }

  _setupEspresso(signal) {
    const panel = this.querySelector('[data-brew-panel="espresso"]');
    if (!panel) return;

    const input = panel.querySelector('[data-brew-dose-input]');
    const dose = panel.querySelector('[data-brew-dose]');
    const yieldOut = panel.querySelector('[data-brew-yield]');
    const output = panel.querySelector('[data-brew-output]');
    const ratio = Number(panel.dataset.ratio) || 2;

    if (!input) return;

    const render = () => {
      const value = Number(input.value);
      const doseLabel = Number.isInteger(value) ? value : value.toFixed(1);
      const yieldLabel = Math.round(value * ratio);

      if (dose) dose.textContent = doseLabel;
      if (yieldOut) yieldOut.textContent = yieldLabel;
      if (output) output.textContent = `${doseLabel} g → ${yieldLabel} g`;
    };

    input.addEventListener('input', render, { signal });
    render();
  }
}

if (!window.customElements.get('brew-guide')) {
  window.customElements.define('brew-guide', BrewGuide);
}
